import { useEffect, useRef, useState } from 'react'
import { reduced } from '../lib/motion'

/** The welcome after sign-in. Full: the speckles of the star fly in from all sides and settle into the star, one glint
 *  crosses it, the greeting fades in, then `onDone`. Short (after the idle lock): the star scales in, greeting, done.
 *  A tap anywhere ends it at once. Under Reduce Motion the still star shows and it ends after a beat.
 *  Nothing here is on the critical path: the caller renders Home behind it as soon as it likes. */
/** The black screen with the pulsing star that stands in for "One moment" while the clock state is checked after a correct ID.
 *  Same star, same size and place as Stardust's, so the two read as one: the pulse fades as the speckles fly in. */
export function StarPulse() {
  return <div className="stardust" role="presentation"><img className="stardust__pulse" src="/brand/star-white.png" alt="" /></div>
}

export function Stardust({ name, short, onDone }: { name: string; short?: boolean; onDone: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const done = useRef(false)
  const [greet, setGreet] = useState(false)
  const finish = () => { if (done.current) return; done.current = true; onDone() }
  const hour = new Date().getHours()
  const hello = short ? `Welcome back, ${name}` : `${hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'}, ${name}`
  useEffect(() => {
    const still = reduced()
    if (still || short) {
      const a = window.setTimeout(() => setGreet(true), still ? 0 : 120)
      const b = window.setTimeout(finish, still ? 500 : 900)
      return () => { window.clearTimeout(a); window.clearTimeout(b) }
    }
    const cv = canvas.current
    if (!cv) { finish(); return }
    let raf = 0, alive = true
    const timers: number[] = []
    const img = new Image()
    img.src = '/brand/star-white.png'
    const run = () => {
      if (!alive) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2), W = 320, H = 260, SW = 150
      cv.width = W * dpr; cv.height = H * dpr; cv.style.width = W + 'px'; cv.style.height = H + 'px'
      const ctx = cv.getContext('2d')
      if (!ctx) { finish(); return }
      const sh = Math.round(SW * img.height / img.width)
      const off = document.createElement('canvas'); off.width = SW * dpr; off.height = sh * dpr
      const o = off.getContext('2d')!; o.drawImage(img, 0, 0, off.width, off.height)
      const d = o.getImageData(0, 0, off.width, off.height).data
      const ox = (cv.width - off.width) / 2, oy = (cv.height - off.height) / 2
      // one particle per speckle of the logo; each starts on a ring around the centre and flies in
      const pts: Float32Array[] = []
      for (let y = 0; y < off.height; y++) for (let x = 0; x < off.width; x++) {
        const a = d[(y * off.width + x) * 4 + 3]
        if (a > 40) { const ang = Math.random() * Math.PI * 2, rad = (150 + Math.random() * 320) * dpr; pts.push(new Float32Array([x + ox, y + oy, a, cv.width / 2 + Math.cos(ang) * rad, cv.height / 2 + Math.sin(ang) * rad, Math.random() * 380])) }
      }
      const DUR = 900, SETTLE = 300, GLINT = 520
      const t0 = performance.now()
      const buf = ctx.createImageData(cv.width, cv.height)
      const frame = (now: number) => {
        if (!alive) return
        const t = now - t0, px = buf.data; px.fill(0)
        const gx = t > DUR + SETTLE ? (t - DUR - SETTLE) / GLINT : -1
        for (const p of pts) {
          const k = Math.max(0, Math.min(1, (t - p[5]) / DUR)), e = 1 - Math.pow(1 - k, 3)
          const X = Math.round(p[3] + (p[0] - p[3]) * e), Y = Math.round(p[4] + (p[1] - p[4]) * e)
          if (X < 0 || Y < 0 || X >= cv.width || Y >= cv.height) continue
          let shade = 215
          if (gx >= 0) { const band = (p[0] + p[1] * .45) / (cv.width * 1.45), dd = Math.abs(band - (gx - .15)); if (dd < .08) shade = 215 + 40 * (1 - dd / .08) }
          const j = (Y * cv.width + X) * 4
          px[j] = px[j + 1] = px[j + 2] = shade; px[j + 3] = Math.max(px[j + 3], p[2] * Math.min(1, k * 1.6))
        }
        ctx.putImageData(buf, 0, 0)
        if (t < DUR + SETTLE + GLINT + 250) raf = requestAnimationFrame(frame)
      }
      raf = requestAnimationFrame(frame)
      timers.push(window.setTimeout(() => setGreet(true), DUR + 200))
      timers.push(window.setTimeout(finish, DUR + SETTLE + GLINT + 350))
    }
    if (img.complete && img.naturalWidth) run(); else { img.onload = run; img.onerror = finish }
    return () => { alive = false; cancelAnimationFrame(raf); timers.forEach(window.clearTimeout) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div className="stardust" onPointerDown={finish} role="presentation">
      {short || reduced()
        ? <img className={'stardust__still' + (short && !reduced() ? ' stardust__still--in' : '')} src="/brand/star-white.png" alt="" />
        : <><canvas ref={canvas} className="stardust__canvas" /><img className="stardust__pulse stardust__pulse--out" src="/brand/star-white.png" alt="" /></>}
      <div className={'stardust__greet' + (greet ? ' in' : '')}>{hello}</div>
    </div>
  )
}
